/**
 * Simulation Web Worker managers — dispatches strata and meridian rendering
 * to Web Workers for non-blocking rendering.
 *
 * Falls back to main-thread renderers if Workers are unavailable.
 *
 * Usage:
 *   import { renderStrataAsync, renderMeridianAsync } from './strataWorkerManager';
 */

import { renderStrata } from './strata';
// meridian uses dynamic imports for fallback (renderMeridian, renderMeridianAnimated)

// ═══════════════════════════════════════════════════════════
//  STRATA WORKER
// ═══════════════════════════════════════════════════════════

let strataWorker = null;
let strataSupported = true;
let strataPendingResolve = null;
let strataPendingReject = null;

function getStrataWorker() {
  if (!strataSupported) return null;
  if (strataWorker) return strataWorker;
  try {
    strataWorker = new Worker(new URL('./strata.worker.js', import.meta.url));
    strataWorker.onmessage = (e) => {
      if (e.data.error) {
        if (strataPendingReject) strataPendingReject(new Error(e.data.error));
      } else {
        if (strataPendingResolve) strataPendingResolve(e.data);
      }
      strataPendingResolve = null;
      strataPendingReject = null;
    };
    strataWorker.onerror = (err) => {
      console.warn('[StrataWorker] Worker error:', err);
      if (strataPendingReject) strataPendingReject(err);
      strataPendingResolve = null;
      strataPendingReject = null;
      strataSupported = false;
      strataWorker = null;
    };
    return strataWorker;
  } catch (err) {
    console.warn('[StrataWorker] Worker creation failed:', err);
    strataSupported = false;
    return null;
  }
}

export async function renderStrataAsync(ctx, W, H, palette, params, seed) {
  const w = getStrataWorker();
  if (!w) {
    const { createRandom } = await import('../seededRandom');
    const rand = createRandom(seed);
    renderStrata(ctx, W, H, palette, params, rand);
    return;
  }
  const workerPalette = { colors: palette.colors, bg: palette.bg };
  try {
    const result = await new Promise((resolve, reject) => {
      if (strataPendingReject) strataPendingReject(new Error('Superceded'));
      strataPendingResolve = resolve;
      strataPendingReject = reject;
      w.postMessage({ W, H, palette: workerPalette, params, seed });
    });
    const imageData = new ImageData(new Uint8ClampedArray(result.imageData), result.W, result.H);
    ctx.putImageData(imageData, 0, 0);
  } catch (err) {
    if (err.message === 'Superceded') return;
    console.warn('[StrataWorker] Fallback:', err);
    const { createRandom } = await import('../seededRandom');
    const rand = createRandom(seed);
    renderStrata(ctx, W, H, palette, params, rand);
  }
}

export function isStrataWorkerAvailable() {
  return strataSupported && typeof Worker !== 'undefined';
}

// ═══════════════════════════════════════════════════════════
//  MERIDIAN WORKER — with progressive layer-by-layer animation
// ═══════════════════════════════════════════════════════════

let meridianWorker = null;
let meridianSupported = true;
let meridianPendingResolve = null;
let meridianPendingReject = null;
let meridianOnProgress = null;

function getMeridianWorker() {
  if (!meridianSupported) return null;
  if (meridianWorker) return meridianWorker;
  try {
    meridianWorker = new Worker(new URL('./meridian.worker.js', import.meta.url));
    meridianWorker.onmessage = (e) => {
      if (e.data.error) {
        if (meridianPendingReject) meridianPendingReject(new Error(e.data.error));
        meridianPendingResolve = null;
        meridianPendingReject = null;
        meridianOnProgress = null;
      } else if (e.data.type === 'progress') {
        // Intermediate layer frame — draw it to canvas via callback
        if (meridianOnProgress) {
          const imgData = new ImageData(
            new Uint8ClampedArray(e.data.imageData), e.data.W, e.data.H
          );
          meridianOnProgress(imgData, e.data.layer, e.data.totalLayers);
        }
      } else if (e.data.type === 'complete') {
        // Final frame
        if (meridianPendingResolve) meridianPendingResolve(e.data);
        meridianPendingResolve = null;
        meridianPendingReject = null;
        meridianOnProgress = null;
      }
    };
    meridianWorker.onerror = (err) => {
      console.warn('[MeridianWorker] Worker error:', err);
      if (meridianPendingReject) meridianPendingReject(err);
      meridianPendingResolve = null;
      meridianPendingReject = null;
      meridianOnProgress = null;
      meridianSupported = false;
      meridianWorker = null;
    };
    return meridianWorker;
  } catch (err) {
    console.warn('[MeridianWorker] Worker creation failed:', err);
    meridianSupported = false;
    return null;
  }
}

/**
 * Render meridian via Web Worker with progressive animation.
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} W
 * @param {number} H
 * @param {Object} palette — { colors, bg }
 * @param {Object} params
 * @param {number} seed
 * @param {function} [onProgress] — called with (ImageData, layer, totalLayers) for each intermediate frame
 */
export async function renderMeridianAsync(ctx, W, H, palette, params, seed, onProgress) {
  const w = getMeridianWorker();
  if (!w) {
    // Fallback: main thread animated render
    const { createRandom } = await import('../seededRandom');
    const { renderMeridianAnimated } = await import('./meridian');
    const rand = createRandom(seed);
    await renderMeridianAnimated(ctx, W, H, palette, params, rand);
    return;
  }
  const workerPalette = { colors: palette.colors, bg: palette.bg };
  try {
    const result = await new Promise((resolve, reject) => {
      if (meridianPendingReject) meridianPendingReject(new Error('Superceded'));
      meridianPendingResolve = resolve;
      meridianPendingReject = reject;
      // Wire up progress callback — draws each layer frame to the canvas
      meridianOnProgress = (imgData, layer, total) => {
        ctx.putImageData(imgData, 0, 0);
        if (onProgress) onProgress(layer, total);
      };
      w.postMessage({ W, H, palette: workerPalette, params, seed });
    });
    const imageData = new ImageData(new Uint8ClampedArray(result.imageData), result.W, result.H);
    ctx.putImageData(imageData, 0, 0);
  } catch (err) {
    if (err.message === 'Superceded') return;
    console.warn('[MeridianWorker] Fallback to animated main-thread:', err);
    const { createRandom } = await import('../seededRandom');
    const { renderMeridianAnimated } = await import('./meridian');
    const rand = createRandom(seed);
    await renderMeridianAnimated(ctx, W, H, palette, params, rand);
  }
}

export function isMeridianWorkerAvailable() {
  return meridianSupported && typeof Worker !== 'undefined';
}
